import React, { useState } from 'react';
import { useMutation } from 'hooks/apollo';
import { gql } from '@apollo/client';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { dayjs } from 'shared/utils/date';
import { Form, Row, Col, Button, Select, Input, Radio, DatePicker, Checkbox, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import TagGroup from 'components/TagGroup';

import useFormatMessage from 'hooks/useFormatMessage';
import useGlobalContext from 'hooks/useGlobalContext';
import { ReportDistributionFrequency } from 'shared/prisma/types';
import FormLabel from 'components/FormLabel';
import {
  isValidInviteEmailRaw,
  normalizeRecipientEmailInput,
  splitRecipientInputLines,
} from 'utils/inviteEmailInput';
import messages from '../messages';

const SAVE_DELIVERY = gql`
  mutation EditReport_SaveDelivery($input: UpdateReportDeliveryInput) {
    updateReportDelivery(input: $input) {
      id
      title
      deliveryMethod
      frequency
      startDateTime
      endDateTime
      includePDF
      textResponses
      recipients
    }
  }
`;

const SEND_TEST = gql`
  mutation EditReport_SendTest($reportDistributionId: ID!, $recipients: [String!]!) {
    sendReport(reportDistributionId: $reportDistributionId, recipients: $recipients)
  }
`;

const frequencyOptions = Object.entries(ReportDistributionFrequency).map(([key, value]) => ({
  value,
  label: key,
}));

const EditDelivery = ({ report, form, onDone }) => {
  const [saveDelivery] = useMutation(SAVE_DELIVERY);
  const { message } = useGlobalContext();
  const $t = useFormatMessage();
  const [sendTest, { loading: sendTestLoading }] = useMutation(SEND_TEST);
  const [testEmail, setTestEmail] = useState('');

  const distribution = report.distributions[0] || {};
  const deliveries = distribution.deliveries || [];
  const sendTestEmail = async () => {
    if (!testEmail) return;
    const lines = splitRecipientInputLines(testEmail);
    if (!lines.length || lines.some(line => !isValidInviteEmailRaw(line))) {
      message.error($t(messages.invalidEmailMessage));
      return;
    }
    const recipients = [...new Set(lines.map(normalizeRecipientEmailInput))];

    await sendTest({
      variables: {
        reportDistributionId: distribution.id,
        recipients,
      },
    });

    setTestEmail('');
    message.success($t(messages.sendTestEmailSuccess));
  };

  const onFinish = async (values) => {
    try {
      await saveDelivery({
        variables: {
          input: {
            reportId: report.id,
            title: values.emailSubject,
            startDateTime: values.startDate,
            endDateTime: values.endDate,
            frequency: values.frequency,
            recipients: [...new Set((values.recipients || []).map(normalizeRecipientEmailInput))],
            includePDF: values.includePDF,
            textResponses: values.textResponses,
            deliveryMethod: 'EMAIL',
          },
        },
      });

      message.success($t(messages.deliverySavedMessage));
    } catch (e) {
      console.error(e);
      message.error($t(messages.errorSaving));
    }
    if (onDone) {
      onDone();
    }
  };

  const initialValues = {
    emailSubject: distribution.title,
    frequency: distribution.frequency || 'MONTHLY',
    startDate: distribution.startDateTime ? dayjs(distribution.startDateTime) : null,
    endDate: distribution.endDateTime ? dayjs(distribution.endDateTime) : null,
    includePDF: distribution.includePDF,
    textResponses: distribution.textResponses || 'PDF',
    recipients: (distribution.recipients || []).map(normalizeRecipientEmailInput),
  };

  const deliveriesColumns = [{
    title: <FormattedMessage {...messages.deliveryDate} />,
    dataIndex: 'date',
    render: text => dayjs(text).format('L'),
  },
  {
    title: <FormattedMessage {...messages.recipients} />,
    dataIndex: 'recipients',
    render: (_, record) => record.recipients.join(', '),
  },
  {
    key: 'pdf',
    align: 'right',
    render: (_, record) => (
      <a href={`${process.env.MANAGER_BASE_PATH}/download/report/${report.id}/${dayjs(record.date).format('YYYYMMDD')}`} target="_blank">
        <FormattedMessage {...messages.downloadPDF} />
      </a>
    ),
  },
  {
    key: 'preview',
    align: 'right',
    render: (_, record) => (
      <Link to={`/reports/${report.id}/${dayjs(record.date).format('YYYYMMDD')}`}>
        <FormattedMessage {...messages.viewDelivery} />
      </Link>
    ),
  }];

  return (
    <Form layout="vertical" form={form} onFinish={onFinish} initialValues={initialValues}>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="emailSubject" label={$t(messages.emailSubjectFieldLabel)}>
            <Input
              placeholder={$t(messages.emailSubjectFieldPlaceholder)}
              suffix={(
                <Tooltip placement="topRight" title={$t(messages.emailSubjectFieldHelp, { variable: '{PERIOD}' })}>
                  <InfoCircleOutlined />
                </Tooltip>
              )}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="frequency" label={$t(messages.frequency)}>
            <Select options={frequencyOptions} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="startDate" label={$t(messages.deliveryStartFieldLabel)}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="endDate" label={$t(messages.deliveryEndFieldLabel)}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="includePDF" label={$t(messages.pdf)} valuePropName="checked">
            <Checkbox>{$t(messages.pdfAttachmentCheckboxLabel)}</Checkbox>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="textResponses" label={$t(messages.deliveryTextResponsesLabel)}>
            <Radio.Group buttonStyle="solid">
              <Radio.Button value="PDF"><FormattedMessage {...messages.pdf} /></Radio.Button>
              <Radio.Button value="XLS"><FormattedMessage {...messages.xls} /></Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="recipients"
            label={$t(messages.recipients)}
            valuePropName="tags"
            rules={[
              {
                validator: (_, recipients = []) => {
                  const invalid = recipients.some(recipient => !isValidInviteEmailRaw(recipient));
                  if (invalid) {
                    return Promise.reject(new Error($t(messages.invalidEmailMessage)));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TagGroup
              newTagLabel={$t(messages.addRecipientTagLabel)}
              validateTag={isValidInviteEmailRaw}
              normalizeTag={normalizeRecipientEmailInput}
              onValidationFail={() => message.error($t(messages.invalidEmailMessage))}
            />
          </Form.Item>
        </Col>
        {distribution.id && (
          <Col span={12}>
            <Form.Item label={<FormLabel label={messages.sendTestEmailFieldLabel} description={messages.sendTestEmailFieldDescription} />}>
              <div className="send-test">
                <Input
                  className="email-input"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                  placeholder={$t(messages.sendTestEmailFieldPlaceholder)}
                />
                <Button
                  disabled={!testEmail}
                  loading={sendTestLoading}
                  onClick={sendTestEmail}
                >
                  {$t(messages.sendTestEmailButtonLabel)}
                </Button>
              </div>
            </Form.Item>
          </Col>
        )}
      </Row>
      <Row>
        <Col span={24}>
          <Form.Item label={$t(messages.pastDeliveries)}>
            <Table
              size="small"
              bordered
              emptyMessage={<FormattedMessage {...messages.noPastDeliveries} />}
              columns={deliveriesColumns}
              dataSource={deliveries}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default EditDelivery;
